import { GET_BASE_URL } from '../constants/apiEndpoints';
import { buildLoungeAuthHeaders } from './loungeAuthHeader';

type ResortPostLookup = {
  isWdw: boolean;
  chatRoomId: string;
  userId: string;
};

type PostedPost = {
  chat_id?: number;
  chat_msg?: string;
  chat_time?: string;
  mapping_url?: string;
  user_id?: number;
  user?: {
    user_id?: number;
  };
};

type PostedPostSubmitPayload = {
  chat_id?: number | string;
  mapping_url?: string;
};

type WaitForPostedPostParams = ResortPostLookup & {
  token: string;
  chatMsg: string;
  createdAt: number;
  attempts?: number;
  delayMs?: number;
  fetchImpl?: typeof fetch;
};

export function normalizePostedText(value: unknown): string {
  return String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

export function buildPostedPostLookupUrl({
  isWdw,
  chatRoomId,
  userId,
}: ResortPostLookup): string {
  const base = isWdw
    ? `${GET_BASE_URL}/api/v1/disneyworldHome`
    : `${GET_BASE_URL}/api/v1/home`;
  const roomPath =
    !isWdw && chatRoomId === '7'
      ? ''
      : isWdw && chatRoomId === '1'
        ? ''
        : `/${chatRoomId}`;

  const params = new URLSearchParams({
    page: '1',
    sortordefault: 'ww',
    user_id: userId,
  });

  return `${base}${roomPath}?${params.toString()}`;
}

export function buildPostedPostDetailHref(
  isWdw: boolean,
  chatId: number,
  mappingUrl?: string
): string {
  const base = isWdw ? '/disneyworld/lands-talk/' : '/disneyland/lands-talk/';
  return `${base}${mappingUrl || `${chatId}/Mousewait`}`;
}

export function buildPostedPostFeedHref(isWdw: boolean, chatRoomId: string): string {
  const id = String(chatRoomId);

  if (isWdw) {
    switch (id) {
      case '0':
        return '/disneyworld/lands/0/the-hub/';
      case '2':
        return '/disneyworld/lands/2/Wdw-Real-Time/';
      case '1':
      default:
        return '/disneyworld/lounge/';
    }
  }

  switch (id) {
    case '0':
      return '/disneyland/lands/0/the-hub/';
    case '1':
      return '/disneyland/lands/1/Disneyland-Real-Time/';
    case '3':
      return '/loungeland/';
    case '4':
      return '/club333/';
    case '7':
    default:
      return '/disneyland/lounge/';
  }
}

export function postedPostFromSubmitPayload(
  payload: PostedPostSubmitPayload | undefined
): PostedPost | undefined {
  const chatId = Number(payload?.chat_id || 0);

  if (!chatId) {
    return undefined;
  }

  return {
    chat_id: chatId,
    mapping_url: payload?.mapping_url,
  };
}

export function findMatchingPostedPost(
  posts: PostedPost[],
  userId: string,
  chatMsg: string,
  createdAt: number
): PostedPost | undefined {
  const normalizedMessage = normalizePostedText(chatMsg);
  const userPosts = posts.filter((post) => {
    const postUserId = post.user?.user_id ?? post.user_id;
    return String(postUserId || '') === String(userId || '');
  });

  return (
    userPosts.find(
      (post) => normalizePostedText(post.chat_msg) === normalizedMessage
    ) ||
    userPosts.find((post) => {
      const time = post.chat_time ? new Date(post.chat_time).getTime() : 0;
      return Boolean(time) && Math.abs(time - createdAt) < 5 * 60 * 1000;
    }) ||
    userPosts[0]
  );
}

const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

export async function waitForPostedPost({
  isWdw,
  chatRoomId,
  userId,
  token,
  chatMsg,
  createdAt,
  attempts = 8,
  delayMs = 850,
  fetchImpl = fetch,
}: WaitForPostedPostParams): Promise<PostedPost | undefined> {
  const url = buildPostedPostLookupUrl({ isWdw, chatRoomId, userId });

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const response = await fetchImpl(url, {
      headers: {
        ...buildLoungeAuthHeaders(token),
      },
    });
    const body = await response.json();
    const posts = Array.isArray(body?.data) ? body.data : [];
    const matched = findMatchingPostedPost(posts, userId, chatMsg, createdAt);

    if (matched?.chat_id) {
      return matched;
    }

    if (attempt < attempts - 1) {
      await sleep(delayMs + attempt * 250);
    }
  }

  return undefined;
}
